import { UserRole } from '@/components/authnz/types/rbac';
import type { AccessState } from '@/lib/access-state';
import { APP_ACCESS_ROLES, userHasAppAccess } from '@/lib/access-state';

export type SignupGateVariant =
  | 'invited-waiting'
  | 'payment-pending'
  | 'unverified-email'
  | 'clients-only';

export interface SignupGateCopy {
  variant: SignupGateVariant;
  title: string;
  message: string;
  detail?: string;
}

export function resolveSignupGateCopy(params: {
  roles?: UserRole[] | string[] | null;
  accessState?: AccessState | string | null;
  emailVerified?: boolean;
  reasonKind?: string | null;
}): SignupGateCopy {
  const roles = (params.roles ?? []) as UserRole[];

  if (params.reasonKind === 'unverified-email' || params.emailVerified === false) {
    return {
      variant: 'unverified-email',
      title: 'Verify your email',
      message: 'Verify this email address before using Harry Biomechanics.',
      detail: 'Check your inbox for a verification link from your sign-in provider, then sign in again.',
    };
  }

  if (roles.length > 0 && !userHasAppAccess(roles)) {
    const onlyClient =
      roles.every((role) => role === UserRole.CLIENT) ||
      (roles.includes(UserRole.CLIENT) &&
        roles.every((role) => role === UserRole.CLIENT || !APP_ACCESS_ROLES.includes(role as UserRole)));
    if (onlyClient) {
      return {
        variant: 'clients-only',
        title: 'Practitioner app only',
        message: 'This sign-in is for the practitioner app. Your account is set up as a client.',
        detail: 'Sign out and use the client experience, or contact your administrator if you need practitioner access.',
      };
    }
  }

  if (params.accessState === 'payment-pending') {
    return {
      variant: 'payment-pending',
      title: 'Payment processing',
      message: 'Your subscription payment is still being confirmed.',
      detail: 'This usually completes within a couple of minutes. If access does not appear, contact support with your purchase email.',
    };
  }

  return {
    variant: 'invited-waiting',
    title: 'Access required',
    message: 'Your account does not have practitioner access yet.',
    detail: 'Choose a plan to get started, or contact your administrator if you were already invited.',
  };
}
